import React, { useMemo, useState } from "react";
import { Box, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import CollapsibleSidebar from "../../../component/common/CollapsibleSidebar";
import FormTreeNav from "../FormTreeNav";
import { buildFormTree, folderColor, groupsFromAdminList } from "../formTree";

/**
 * 관리자 양식 목록 — 상신 모달과 같은 폴더 트리를 쓴다.
 *
 * 두 화면이 같은 트리를 보는 게 중요하다. 관리자가 «지출 결의서» 폴더에서 고른 양식이
 * 직원 화면에서는 다른 자리에 있으면 "그 양식이 어디 갔지" 가 된다.
 *
 * 배지 둘은 서로 다른 뜻이라 색을 나눠 뒀다.
 *  - «초안» : 저장했지만 발행 안 함 → 상신 화면은 아직 예전 내용을 쓴다
 *  - «중지» : 신규 상신 목록에서 빠짐 → 기존 문서 조회는 계속된다
 */
export default function FormListSidebar({ forms, selectedId, onSelect, dirtyId }) {
    const [q, setQ] = useState("");

    /**
     * 접힘 레일에서 고른 그룹. {@code seq} 가 있어야 같은 그룹을 다시 골라도 트리가 반응한다
     * (문자열만 넘기면 값이 안 바뀌어 두 번째 클릭이 무시된다).
     */
    const [focusGroup, setFocusGroup] = useState(null);

    const groups = useMemo(() => {
        const kw = q.trim().toLowerCase();
        const hit = (forms || []).filter((f) =>
            !kw || `${f.name} ${f.group} ${f.code}`.toLowerCase().includes(kw));
        return groupsFromAdminList(hit);
    }, [forms, q]);

    return (
        <CollapsibleSidebar
            storageKey="approvalForm.sidebar"
            width={268}
            expandTitle="양식 목록 펼치기"
            collapseTitle="양식 목록 접기"
            renderCollapsed={() => (
                <FormGroupRail
                    groups={groups}
                    dirtyId={dirtyId}
                    onPick={(code) => setFocusGroup({ code, seq: Date.now() })}
                />
            )}
            sx={{ borderRight: "1px solid #e2e8f0" }}
        >
            {/* pt 를 줄였다 — 위에 토글 줄이 하나 생겨 원래 여백을 그대로 두면 검색창이 내려앉는다. */}
            <Box sx={{ px: 1.25, pt: 0.5, pb: 1.25, borderBottom: "1px solid #f1f5f9" }}>
                <TextField
                    size="small" fullWidth value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="양식 검색"
                    InputProps={{ startAdornment: (
                        <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ fontSize: 17, color: "#94a3b8" }} />
                        </InputAdornment>
                    ) }}
                />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", py: 0.5 }}>
                <FormTreeNav
                    groups={groups}
                    keyOf={(f) => f.id}
                    selected={selectedId}
                    onSelect={onSelect}
                    query={q}
                    focusGroup={focusGroup}
                    renderRight={(f) => (
                        <Stack direction="row" spacing={0.4} sx={{ flexShrink: 0 }}>
                            {(f.hasDraft || f.id === dirtyId) && <Badge text="초안" color="#b45309" bg="#fef3c7" />}
                            {!f.active && <Badge text="중지" color="#64748b" bg="#f1f5f9" />}
                        </Stack>
                    )}
                />
                {groups.length === 0 && (
                    <Typography sx={{ p: 2, fontSize: 12, color: "#94a3b8" }}>검색 결과 없음</Typography>
                )}
            </Box>
        </CollapsibleSidebar>
    );
}

/**
 * 접힘(48px) 레일 — <b>최상위 그룹 폴더만</b>.
 *
 * <h3>왜 그룹만인가</h3>
 * <p>다른 셋과 달리 여기는 3층 트리(그룹 → 폴더 → 양식 19종)다. 48px 에 전부 세우면 아이콘이
 * 스무 개 넘게 쌓이는데 층 정보(들여쓰기·↳)가 사라져 <b>평평한 아이콘 더미</b>가 된다.
 * 그러면 이름을 지운 대가로 얻는 게 없다 — 어차피 Tooltip 을 하나씩 짚어 봐야 한다.
 * 그룹은 둘뿐이라(«지출»·«일반») 한눈에 세어지고, 고른 다음 펼쳐 그 안에서 찾으면 된다.
 *
 * <h3>배지 처리 — «초안» 은 점으로, «중지» 는 버린다</h3>
 * <p>둘을 같이 축약하면 그룹마다 점이 둘씩 붙어 결국 아이콘이 시끄러워진다. 그래서 성격으로 갈랐다.
 * <ul>
 *   <li><b>초안</b>은 <i>할 일</i>이다 — "저장만 하고 발행을 안 했다" 는 신호라 놓치면 관리자가
 *       고친 줄 알고 넘어간다. 이 화면이 존재하는 이유와 직결되므로 그룹 단위로 모아 점 하나를 남긴다.
 *       (안읽음 뱃지를 접힘에서 남기는 것과 같은 판단 — 남은 일은 접어도 보여야 한다.)</li>
 *   <li><b>중지</b>는 <i>상태</i>다. 개별 양식의 고정된 성질이라 "이 그룹에 중지된 게 하나 있다" 를
 *       알아도 할 일이 생기지 않고, 19종 중 몇 개는 늘 중지라 거의 모든 그룹에 상시 켜져 신호가 죽는다.
 *       접으면 버리고 펼쳤을 때 양식 줄에서 본다.</li>
 * </ul>
 *
 * <p>Tooltip 은 필수다. 최상위 폴더 색은 {@code folderColor(name, 0)} 이 전부 같은 노랑을 주므로
 * (하위 폴더만 뜻에 따라 색을 나눈다) 아이콘만으로는 그룹을 구분할 수 없다.
 */
function FormGroupRail({ groups, dirtyId, onPick }) {
    // 펼침이 실제로 그리는 것과 같은 트리를 쓴다 — 양식이 없어 걸러진 그룹이 레일에만 남으면
    // 눌러도 아무 데도 못 간다.
    const tree = useMemo(() => buildFormTree(groups), [groups]);

    const hasDraft = (code) => {
        const g = (groups || []).find((x) => x.groupCode === code);
        return (g?.forms || []).some((f) => f.hasDraft || f.id === dirtyId);
    };

    return (
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 0.5, pb: 1 }}>
            {tree.map((g) => {
                const total = g.nodes.reduce(
                    (n, x) => n + (x.kind === "folder" ? x.forms.length : 1), 0);
                return (
                    <Tooltip
                        key={g.groupCode}
                        title={`${g.groupName} · 양식 ${total}개${hasDraft(g.groupCode) ? " · 초안 있음" : ""}`}
                        placement="right"
                    >
                        <Box
                            onClick={() => onPick(g.groupCode)}
                            sx={{
                                position: "relative",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                height: 36, mx: 0.5, borderRadius: 1.2, cursor: "pointer",
                                "&:hover": { bgcolor: "#f1f5f9" },
                                transition: "background-color 0.12s",
                            }}
                        >
                            <FolderRoundedIcon sx={{ fontSize: 20, color: folderColor(g.groupName, 0) }} />
                            {/* 초안 점 — 배지의 글자색(#b45309)을 그대로 쓴다. 폴더가 같은 계열 노랑이라
                                흰 테두리를 둘러야 겹친 자리에서 점으로 읽힌다. */}
                            {hasDraft(g.groupCode) && (
                                <Box sx={{
                                    position: "absolute", top: 5, right: 8,
                                    width: 8, height: 8, borderRadius: "50%",
                                    bgcolor: "#b45309", border: "1.5px solid #fff",
                                }} />
                            )}
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

function Badge({ text, color, bg }) {
    return (
        <Box sx={{ px: 0.6, py: 0.1, borderRadius: 0.75, bgcolor: bg, color,
                   fontSize: 10, fontWeight: 800 }}>
            {text}
        </Box>
    );
}
